import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X, Download, Loader2, CheckCircle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import EmailEditModal from './EmailEditModal';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ============ SIGNATURE PAD COMPONENT ============
const SignaturePad = ({ onChange, initialValue, testId }) => {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const lastPointRef = useRef(null);

  const getCanvas = () => canvasRef.current;

  const resize = useCallback(() => {
    const canvas = getCanvas();
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext('2d');
    ctx.scale(ratio, ratio);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.strokeStyle = '#0B1623';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
  }, []);

  useEffect(() => {
    resize();
    // If initialValue provided, draw it
    if (initialValue) {
      const img = new Image();
      img.onload = () => {
        const ctx = getCanvas().getContext('2d');
        const rect = getCanvas().getBoundingClientRect();
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = initialValue;
    }
  }, [resize, initialValue]);

  const getPos = (e) => {
    const canvas = getCanvas();
    const rect = canvas.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    return { x: clientX - rect.left, y: clientY - rect.top };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawingRef.current = true;
    lastPointRef.current = getPos(e);
  };

  const draw = (e) => {
    if (!isDrawingRef.current) return;
    e.preventDefault();
    const canvas = getCanvas();
    const ctx = canvas.getContext('2d');
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    lastPointRef.current = p;
  };

  const endDraw = () => {
    if (!isDrawingRef.current) return;
    isDrawingRef.current = false;
    if (onChange) onChange(getCanvas().toDataURL('image/png'));
  };

  const clear = () => {
    const canvas = getCanvas();
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, rect.width, rect.height);
    if (onChange) onChange('');
  };

  return (
    <div className="space-y-2">
      <div className="bg-white rounded border-2 border-dashed border-slate-400">
        <canvas
          ref={canvasRef}
          className="w-full h-32 cursor-crosshair touch-none"
          onMouseDown={startDraw}
          onMouseMove={draw}
          onMouseUp={endDraw}
          onMouseLeave={endDraw}
          onTouchStart={startDraw}
          onTouchMove={draw}
          onTouchEnd={endDraw}
          data-testid={testId}
        />
      </div>
      <button
        type="button"
        onClick={clear}
        className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
      >
        <Trash2 className="w-3 h-3" /> Limpiar firma
      </button>
    </div>
  );
};

// ============ MAIN MODAL ============
export default function DeliveryLetterModal({ quotationId, onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [signing, setSigning] = useState(false);
  const [dl, setDl] = useState(null);
  const [repSignature, setRepSignature] = useState('');
  const [savedRepSignature, setSavedRepSignature] = useState(''); // from user profile
  const [useStoredSig, setUseStoredSig] = useState(true);
  const [repName, setRepName] = useState('Alejandro Olivos');
  const [repPosition, setRepPosition] = useState('Administrador Único');
  const [sendLinkModal, setSendLinkModal] = useState(null);
  const [sendLinkEmail, setSendLinkEmail] = useState('');
  const [sendingLink, setSendingLink] = useState(false);
  const [sendFinalModal, setSendFinalModal] = useState(null);
  const [sendFinalEmail, setSendFinalEmail] = useState('');
  const [sendingFinal, setSendingFinal] = useState(false);
  const [q_client_email, setQClientEmail] = useState('');

  useEffect(() => {
    if (sendLinkModal) setSendLinkEmail(sendLinkModal.currentEmail || '');
  }, [sendLinkModal]);
  useEffect(() => {
    if (sendFinalModal) setSendFinalEmail(sendFinalModal.currentEmail || '');
  }, [sendFinalModal]);

  // Fetch on mount
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`);
        const data = await r.json();
        if (data.success) {
          setDl(data.delivery_letter);
          setQClientEmail(data.client_email || '');
          if (data.rep_signature) setSavedRepSignature(data.rep_signature);
          if (data.rep_name) setRepName(data.rep_name);
          if (data.rep_position) setRepPosition(data.rep_position);
          // Pre-fill from existing
          const sigs = data.delivery_letter.signatures || {};
          if (sigs.representative?.image_b64) setRepSignature(sigs.representative.image_b64);
        } else {
          toast.error(data.detail || "Error");
        }
      } catch (e) {
        toast.error("Error cargando acta: " + e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [quotationId]);

  const update = (patch) => setDl(prev => ({ ...prev, ...patch }));
  const updateChecklist = (key, val) => setDl(prev => ({
    ...prev,
    checklist: { ...(prev.checklist || {}), [key]: val }
  }));

  const saveData = async () => {
    setSaving(true);
    try {
      const payload = {
        vehicle_description: dl.vehicle_description || '',
        vin: dl.vin || '',
        engine_number: dl.engine_number || '',
        color_ext: dl.color_ext || '',
        color_int: dl.color_int || '',
        invoice_number: dl.invoice_number || '',
        client_name: dl.client_name || '',
        recipient_name: dl.recipient_name || '',
        recipient_position: dl.recipient_position || '',
        delivery_place: dl.delivery_place || '',
        delivery_date: dl.delivery_date || '',
        checklist: dl.checklist || {}
      };
      const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await r.json();
      if (data.success) {
        setDl(data.delivery_letter);
        toast.success("Datos del acta guardados");
        return true;
      }
      toast.error(data.detail || "Error guardando");
      return false;
    } catch (e) {
      toast.error("Error: " + e.message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const signRole = async (role, image_b64, name, position) => {
    if (!image_b64) {
      toast.error("Dibuja la firma primero");
      return false;
    }
    if (!name.trim()) {
      toast.error("Captura el nombre del firmante");
      return false;
    }
    setSigning(true);
    try {
      const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role, image_b64, name, position })
      });
      const data = await r.json();
      if (data.success) {
        toast.success(`Firma de ${role === 'representative' ? 'representante' : 'cliente'} registrada`);
        if (data.fully_signed && data.email_result?.success) {
          toast.success(`📧 Acta enviada automáticamente a ${data.email_result.sent_to}`);
        } else if (data.fully_signed && data.email_result && !data.email_result.success) {
          toast.warning(`Acta firmada, pero no se pudo enviar el correo: ${data.email_result.error}`);
        }
        // Refresh
        const r2 = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`);
        const d2 = await r2.json();
        if (d2.success) setDl(d2.delivery_letter);
        if (data.fully_signed && onSaved) onSaved();
        return true;
      }
      toast.error(data.detail || "Error al firmar");
      return false;
    } catch (e) {
      toast.error("Error: " + e.message);
      return false;
    } finally {
      setSigning(false);
    }
  };

  const handleSignRep = async () => {
    const ok = await saveData();
    if (!ok) return;
    const adminName = repName || dl.rep_name || "Alejandro Olivos";
    const adminPos = repPosition || dl.rep_position || "Administrador Único";
    const sigToUse = useStoredSig && savedRepSignature ? savedRepSignature : repSignature;
    if (!sigToUse) {
      toast.error("Dibuja la firma o activa 'Usar mi firma guardada'");
      return;
    }
    await signRole('representative', sigToUse, adminName, adminPos);
  };

  const sendFinalToClient = async () => {
    const email = (sendFinalEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error("Captura un correo válido");
      return;
    }
    setSendingFinal(true);
    try {
      const res = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/send-final`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_email: email })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Acta enviada a ${data.sent_to}`);
        setSendFinalModal(null);
        const r2 = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`);
        const d2 = await r2.json();
        if (d2.success) setDl(d2.delivery_letter);
        if (onSaved) onSaved();
      } else {
        toast.error(data.error || "Error al enviar acta");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally {
      setSendingFinal(false);
    }
  };

  const sendClientSignLink = async () => {
    const email = (sendLinkEmail || '').trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error("Captura un correo válido");
      return;
    }
    setSendingLink(true);
    try {
      const res = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/send-client-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ override_email: email })
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`Link enviado a ${data.sent_to}`);
        setSendLinkModal(null);
        // Refresh
        const r2 = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`);
        const d2 = await r2.json();
        if (d2.success) setDl(d2.delivery_letter);
      } else {
        toast.error(data.error || data.detail || "Error al enviar link");
      }
    } catch (e) {
      toast.error("Error: " + e.message);
    } finally {
      setSendingLink(false);
    }
  };

  const resyncXml = async () => {
    if (!window.confirm('¿Re-extraer datos del XML de factura? Esto sobreescribirá VIN, motor, colores y fecha con los del XML actual.')) return;
    try {
      const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/resync-xml`, { method: 'POST' });
      const data = await r.json();
      if (data.success) {
        toast.success('Datos re-extraídos del XML');
        setDl(data.delivery_letter);
      } else {
        toast.error(data.error || data.detail || 'Error');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
  };

  const resetLetter = async () => {
    if (!window.confirm('¿Reiniciar el acta? Esto borrará las firmas y sellos actuales (los datos se conservan). Úsalo para re-probar el flujo desde cero.')) return;
    try {
      const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/reset`, { method: 'POST' });
      const data = await r.json();
      if (data.success) {
        toast.success('Acta reiniciada');
        // Refetch para que auto-aplique tu firma actual
        const r2 = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter`);
        const d2 = await r2.json();
        if (d2.success) {
          setDl(d2.delivery_letter);
          if (d2.rep_signature) setSavedRepSignature(d2.rep_signature);
        }
        if (onSaved) onSaved();
      } else {
        toast.error(data.detail || data.error || 'Error al reiniciar');
      }
    } catch (e) {
      toast.error('Error: ' + e.message);
    }
  };

  const downloadPdf = async () => {
    try {
      const r = await fetch(`${API}/api/quotations/${quotationId}/delivery-letter/pdf`);
      if (!r.ok) {
        let msg = `HTTP ${r.status}`;
        try {
          const j = await r.json();
          msg = j.detail || j.error || msg;
        } catch (_) { /* not JSON */ }
        throw new Error(msg);
      }
      const blob = await r.blob();
      if (!blob || blob.size === 0) throw new Error("El PDF vino vacío");
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ACTA_ENTREGA_${dl.invoice_number || dl.quotation_number || quotationId}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success("PDF descargado");
    } catch (e) {
      toast.error("Error al descargar PDF: " + e.message);
      console.error("downloadPdf error:", e);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-400" />
      </div>
    );
  }
  if (!dl) return null;

  const sigs = dl.signatures || {};
  const repSigned = !!sigs.representative?.signed_at;
  const cliSigned = !!sigs.client?.signed_at;
  const fullySigned = !!dl.signed_at;

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-slate-900 border border-orange-500/40 rounded-xl w-full max-w-4xl my-8" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-700">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <span className="text-orange-400">📝</span> Acta de Entrega de Unidad
          </h2>
          <div className="flex items-center gap-2">
            {fullySigned && (
              <span className="px-3 py-1 rounded bg-emerald-500/20 text-emerald-400 text-xs font-medium flex items-center gap-1">
                <CheckCircle className="w-4 h-4" /> Firmada por ambas partes
              </span>
            )}
            <button onClick={resyncXml}
              className="px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-sm flex items-center gap-1"
              title="Re-extraer datos del XML de factura (VIN, motor, colores)"
              data-testid="resync-xml-btn">
              🔄 Datos XML
            </button>
            <button onClick={saveData} disabled={saving}
              className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm flex items-center gap-1 disabled:opacity-50"
              data-testid="save-delivery-data-header-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : '💾 Guardar'}
            </button>
            {(repSigned || cliSigned || dl.verification_id) && (
              <button onClick={resetLetter}
                className="px-3 py-1.5 bg-rose-600/80 hover:bg-rose-600 text-white rounded text-sm flex items-center gap-1"
                title="Borrar firmas y sellos para re-probar"
                data-testid="reset-delivery-btn">
                🔄 Reiniciar
              </button>
            )}
            <button onClick={downloadPdf}
              className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm flex items-center gap-1"
              data-testid="download-delivery-pdf-btn">
              <Download className="w-4 h-4" /> PDF
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-5 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Vehicle Data */}
          <section>
            <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wide mb-3">Datos de la Unidad</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Vehículo" value={dl.vehicle_description} onChange={(v) => update({ vehicle_description: v })} />
              <Field label="Número de Serie (VIN)" value={dl.vin} onChange={(v) => update({ vin: v })} placeholder="Ej: LSFAM3AB1TA056112" />
              <Field label="Número de Motor" value={dl.engine_number} onChange={(v) => update({ engine_number: v })} />
              <Field label="Factura" value={dl.invoice_number} onChange={(v) => update({ invoice_number: v })} />
              <Field label="Color exterior" value={dl.color_ext} onChange={(v) => update({ color_ext: v })} />
              <Field label="Color interior" value={dl.color_int} onChange={(v) => update({ color_int: v })} />
            </div>
          </section>

          {/* Delivery */}
          <section>
            <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wide mb-3">Entrega</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Cliente / Empresa" value={dl.client_name} onChange={(v) => update({ client_name: v })} />
              <Field label="Lugar de Entrega" value={dl.delivery_place} onChange={(v) => update({ delivery_place: v })} placeholder="Ej: Talleres de Zaragoza STC METRO" />
              <Field label="Nombre de quien recibe" value={dl.recipient_name} onChange={(v) => update({ recipient_name: v })} />
              <Field label="Cargo" value={dl.recipient_position} onChange={(v) => update({ recipient_position: v })} placeholder="Ej: Representante Legal" />
              <Field type="date" label="Fecha de Entrega" value={dl.delivery_date} onChange={(v) => update({ delivery_date: v })} />
            </div>
          </section>

          {/* Checklist */}
          <section>
            <h3 className="text-sm font-medium text-orange-400 uppercase tracking-wide mb-3">Se entrega con la unidad</h3>
            <div className="bg-slate-800/40 rounded-lg p-4 grid grid-cols-2 gap-2">
              <Check label="Manual del propietario" checked={!!dl.checklist?.manual} onChange={(v) => updateChecklist('manual', v)} testId="chk-manual" />
              <Check label="2 Llaves" checked={!!dl.checklist?.two_keys} onChange={(v) => updateChecklist('two_keys', v)} testId="chk-two-keys" />
              <Check label="Gato hidráulico" checked={!!dl.checklist?.jack} onChange={(v) => updateChecklist('jack', v)} testId="chk-jack" />
              <Check label="Reflejante de seguridad" checked={!!dl.checklist?.reflective} onChange={(v) => updateChecklist('reflective', v)} testId="chk-reflective" />
              <Check label="Póliza de garantía" checked={!!dl.checklist?.warranty} onChange={(v) => updateChecklist('warranty', v)} testId="chk-warranty" />
              <Check label="Llanta de refacción" checked={!!dl.checklist?.spare_tire} onChange={(v) => updateChecklist('spare_tire', v)} testId="chk-spare" />
            </div>
            <div className="mt-3">
              <Field label="Otros (opcional)" value={dl.checklist?.other || ''} onChange={(v) => updateChecklist('other', v)} placeholder="Accesorios adicionales..." />
            </div>
          </section>

          {/* Save button always visible */}
          <div className="flex justify-end">
            <button
              onClick={saveData}
              disabled={saving}
              className="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded text-sm disabled:opacity-50 font-medium flex items-center gap-2"
              data-testid="save-delivery-data-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : '💾 Guardar datos'}
            </button>
          </div>

          {/* Signatures */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2 border-t border-slate-700">
            {/* Representative (auto-applied from stored signature) */}
            <div className="space-y-3 bg-slate-800/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-orange-400">Firma Representante HEBEH</h3>
                {repSigned && (
                  <span className="text-xs text-blue-300 flex items-center gap-1" title="Firma digital aplicada desde tu perfil">
                    <CheckCircle className="w-3 h-3"/>Aplicada
                  </span>
                )}
              </div>
              <p className="text-xs text-slate-400">{repName} — {repPosition}</p>
              {repSigned ? (
                <div className="bg-white rounded p-2">
                  <img src={sigs.representative.image_b64} alt="Firma representante" className="h-24 mx-auto" />
                  <p className="text-xs text-slate-500 text-center mt-1">
                    {sigs.representative.auto_applied
                      ? 'Firma digital guardada aplicada automáticamente'
                      : `Firmada: ${sigs.representative.signed_at?.slice(0, 19).replace('T', ' ')} UTC`}
                  </p>
                </div>
              ) : (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs text-amber-200">
                  No tienes firma digital guardada en tu perfil. Ve a <strong>Usuarios → tu cuenta → Firma</strong> para subir tu firma y que se aplique automáticamente aquí.
                </div>
              )}
            </div>

            {/* Client */}
            <div className="space-y-3 bg-slate-800/30 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-orange-400">Firma del Cliente</h3>
                {cliSigned && <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3 h-3"/>Firmada</span>}
              </div>
              <p className="text-xs text-slate-400">
                {dl.recipient_name || '—'} {dl.recipient_position ? `— ${dl.recipient_position}` : ''}
              </p>
              {cliSigned ? (
                <div className="bg-white rounded p-2">
                  <img src={sigs.client.image_b64} alt="Firma cliente" className="h-24 mx-auto" />
                  <p className="text-xs text-slate-500 text-center mt-1">
                    Firmada: {sigs.client.signed_at?.slice(0, 19).replace('T', ' ')} UTC
                    {sigs.client.signed_via === 'remote_link' && ' • Firma remota'}
                  </p>
                  {sigs.client.ip && (
                    <p className="text-xs text-slate-500 text-center">IP: {sigs.client.ip}</p>
                  )}
                </div>
              ) : (
                <>
                  {dl.client_sign_link_sent_at ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded p-3 text-xs space-y-1">
                      <p className="text-amber-300 font-medium flex items-center gap-1">
                        ⏳ Link enviado, esperando firma
                      </p>
                      <p className="text-slate-300">A: {dl.client_sign_link_sent_to}</p>
                      <p className="text-slate-500">
                        Enviado: {dl.client_sign_link_sent_at?.slice(0, 19).replace('T', ' ')} UTC
                      </p>
                      <button
                        type="button"
                        onClick={() => setSendLinkModal({ currentEmail: dl.client_sign_link_sent_to || '' })}
                        className="mt-2 text-amber-300 hover:text-amber-200 underline"
                      >
                        Reenviar / Cambiar correo
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setSendLinkModal({
                        currentEmail: dl.client_email || q_client_email || '',
                      })}
                      className="w-full py-2.5 bg-blue-500 hover:bg-blue-600 text-white rounded text-sm font-medium flex items-center justify-center gap-2"
                      data-testid="send-sign-link-btn"
                    >
                      ✉️ Enviar al cliente para firma
                    </button>
                  )}
                  <p className="text-xs text-slate-500">
                    El cliente recibirá un link único para firmar desde su dispositivo (con registro de IP, correo y timestamp).
                    <br /><strong>Los sellos (ID, hash y QR) se generan al firmar el cliente.</strong>
                  </p>
                </>
              )}
            </div>
          </section>

          {fullySigned && (
            <section className="bg-emerald-500/10 border border-emerald-500/40 rounded-lg p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-emerald-400" />
                <h3 className="text-sm font-bold text-emerald-300 uppercase tracking-wide">Acta firmada por ambas partes</h3>
              </div>
              {dl.email_sent_at ? (
                <div className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                  📜 <strong>Histórico:</strong> último envío registrado a <strong>{dl.email_sent_to || '—'}</strong>
                  <span className="text-amber-200/70"> • {dl.email_sent_at?.slice(0,19).replace('T',' ')} UTC</span>
                  <div className="text-slate-400 mt-1">No se envía automáticamente — usa el botón de abajo cuando estés listo.</div>
                </div>
              ) : (
                <p className="text-xs text-slate-300">El acta <strong>NO se envió automáticamente</strong>. Revisa los datos y usa el botón para enviarla al cliente.</p>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={downloadPdf}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded text-sm flex items-center gap-2"
                  data-testid="preview-final-pdf-btn"
                >
                  <Download className="w-4 h-4" /> Descargar / Ver PDF
                </button>
                <button
                  onClick={() => setSendFinalModal({ currentEmail: dl.email_sent_to || dl.client_sign_link_sent_to || q_client_email || '' })}
                  className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded text-sm font-medium flex items-center gap-2"
                  data-testid="send-final-to-client-btn"
                >
                  ✉️ Enviar PDF al cliente
                </button>
              </div>
            </section>
          )}

          {fullySigned && dl.verification_id && (
            <div className="text-xs text-slate-500 pt-2 border-t border-slate-700/50 font-mono break-all">
              <div>ID verificación: {dl.verification_id}</div>
              {dl.document_hash && <div>Hash: {dl.document_hash.slice(0, 48)}...</div>}
            </div>
          )}
        </div>
      </div>

      {/* SEND SIGN LINK MODAL */}
      {/* SEND SIGN LINK MODAL (Acta - link firma) */}
      <EmailEditModal
        open={!!sendLinkModal}
        onClose={() => setSendLinkModal(null)}
        onSent={() => { if (onSaved) onSaved(); setSendLinkModal(null); /* refetch parent data */ window.dispatchEvent(new Event('delivery-letter-updated')); }}
        title="Enviar link de firma al cliente"
        sendEndpoint={`/api/quotations/${quotationId}/delivery-letter/send-client-link`}
        initialTo={sendLinkModal?.currentEmail || ''}
        confirmLabel="Enviar link"
        helperText="El cliente recibirá un correo con un link único (válido 7 días) para firmar el acta. Se registrará IP, navegador y firma."
      />

      {/* SEND FINAL PDF MODAL (Acta firmada) */}
      <EmailEditModal
        open={!!sendFinalModal}
        onClose={() => setSendFinalModal(null)}
        onSent={() => { if (onSaved) onSaved(); setSendFinalModal(null); }}
        title="Enviar Acta de Entrega firmada al cliente"
        sendEndpoint={`/api/quotations/${quotationId}/delivery-letter/send-final`}
        initialTo={sendFinalModal?.currentEmail || ''}
        confirmLabel="Enviar PDF"
        helperText="Se adjuntará el PDF firmado por ambas partes con los datos de verificación (ID, Hash, QR)."
      />
    </div>
  );
}

// Small helper components
function Field({ label, value, onChange, placeholder = '', type = 'text' }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-1">{label}</label>
      <input
        type={type}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white text-sm focus:border-orange-500 focus:outline-none"
      />
    </div>
  );
}

function Check({ label, checked, onChange, testId }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer hover:bg-slate-800/50 rounded px-2 py-1.5">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 accent-orange-500"
        data-testid={testId}
      />
      <span className="text-sm text-white">{label}</span>
    </label>
  );
}
